import { IconAlertTriangle, IconPlus, IconTrash } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState } from 'react';
import classNames from 'classnames';
import RenderIf from 'components/RenderIf/RenderIf';
import NSCellInput from '../../../../bricks/NSCellInput/NSCellInput';
import { NSTable, NSInput, NSButton } from '../../../../bricks';
import './MappingTable.scss';
import { HIGHLIGHTED_ROW_NAMES, ProFormaMappingRowNames } from '../../constants';
import {
    IInvalidRow, IInvalidRows, IProFormaMappingRow, ITableColumn,
} from '../../types';

export interface IMappingTableProps {
    data: IProFormaMappingRow[];
    columns: ITableColumn[];
    className?: string;
    getRowKey: (item: IProFormaMappingRow, index: number) => string | number;
    showHeader?: boolean;
    invalidRows: IInvalidRows;
    isMappingDisabled: boolean;
    showMappingErrors: boolean;
    tableName?: string;
    onDeleteRow?: (rowId: string | number) => void;
    addRowCallback?: (tableName?: string) => void;
}

const ProFormaMappingTable = ({
    data, columns, className, getRowKey, showHeader,
    invalidRows, isMappingDisabled, showMappingErrors,
    onDeleteRow, tableName, addRowCallback,
}: IMappingTableProps) => {
    const [hoveredRowId, setHoveredRowId] = useState<string | number | null>(null);
    const renderCell = (column: ITableColumn, item: IProFormaMappingRow, index: number, invalidRow: IInvalidRow) => {
        const sheet = String(item.sheet || '').trim();
        const cell = String(item.cell || '').trim();

        const isBothEmpty = sheet === '' && cell === '';
        const isInvalidSheet = !showMappingErrors && !isBothEmpty && (invalidRow?.sheetError || !!(!invalidRow?.sheetError && item.cell === ''));
        const isInvalidCell = !showMappingErrors && !isBothEmpty && (invalidRow?.cellError || (item.sheet === '' && !invalidRow?.cellError));

        if (column.isInput) {
            return (
                <NSInput
                    name={`${column.key}-${getRowKey(item, index)}`}
                    type="text"
                    value={(item as any)[column.key]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => column.onChange?.(item, e.target.value)}
                    placeholder="—"
                    customClassName={classNames({
                        'border border-danger': !isMappingDisabled && showMappingErrors
                            && ((column.key === 'sheet' && isInvalidSheet) || (column.key === 'cell' && isInvalidCell)),
                    })}
                />
            );
        }
        if (column.isCellEditable && item.name !== ProFormaMappingRowNames.TOTAL_SOURCES) {
            return (
                <div className="d-flex w-100 align-items-center justify-content-between">
                    <NSCellInput
                        type="text"
                        value={(item as any)[column.key]}
                        handleUpdateValue={updatedValue => column.onChange?.(item, updatedValue as string)}
                        placeholder="Enter value"
                    />
                    <NSIcon
                        icon={IconTrash}
                        className={classNames('text-muted cursor--pointer pl-2 hover:text-dark', { invisible: hoveredRowId !== item.id })}
                        onClick={() => onDeleteRow?.(item.id)}
                    />
                </div>
            );
        }
        return (item as any)[column.key] ?? null;
    };

    return (
        <NSTable className={`MappingTable ${className}`}>
            <RenderIf isTrue={showHeader}>
                <thead>
                    <tr>
                        {columns.map(column => (
                            <th key={column.key}>
                                <div className="d-flex justify-content-between align-items-center">
                                    {column.header}
                                    <RenderIf isTrue={column.header === 'Sources'}>
                                        <NSButton
                                            className="text-muted cursor--pointer hover:text-dark"
                                            size="sm"
                                            iconOnly
                                            icon={IconPlus}
                                            callback={() => addRowCallback?.(tableName)}
                                        />
                                    </RenderIf>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
            </RenderIf>
            <tbody>
                {data.map((item, index) => (
                    <tr
                        key={getRowKey(item, index)}
                        className={HIGHLIGHTED_ROW_NAMES.includes(item.name as ProFormaMappingRowNames)
                            ? 'MappingTable--highlighted' : undefined}
                        onMouseEnter={() => setHoveredRowId(item.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                    >
                        {columns.map(column => (
                            <td
                                key={column.key}
                                className={classNames({
                                    'bg-danger-lighten': showMappingErrors && !!invalidRows[item.name as ProFormaMappingRowNames]
                                        && !isMappingDisabled,
                                })}
                            >
                                <div className="d-flex">
                                    <RenderIf isTrue={column.key === 'name' && showMappingErrors
                                        && !!invalidRows[item.name as ProFormaMappingRowNames] && !isMappingDisabled}
                                    >
                                        <NSIcon
                                            icon={IconAlertTriangle}
                                            className="text-danger my-auto mr-2"
                                        />
                                    </RenderIf>
                                    {renderCell(column, item, index, invalidRows[item.name as ProFormaMappingRowNames])}
                                </div>
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </NSTable>
    );
};

export default ProFormaMappingTable;
